import { useRef, useState, useEffect } from 'react';

import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import {
  Box,
  Button,
  ChipInput,
  ChipInputForwardRef,
  Icons,
  Input,
  InputChipProps,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';
import { emailValidation } from '@cast/utils';

import { useCurrentOrganization } from '@cast/infrastructure-organization';
import {
  useInviteOrganizationMutation,
  useUpdateOrganizationMutation,
} from '@oneclick/hooks/mutations/organization';

import { BrowserImage } from './BrowserImage';
import { CardShell } from '../../_components/CardShell';

const noop = () => {};

type Props = {
  onNext?: () => void;
};

export const Step1 = ({ onNext = noop }: Props) => {
  const currentOrganization = useCurrentOrganization();
  const [orgName, setOrgName] = useState(currentOrganization?.name ?? '');
  const [emailChips, setEmailChips] = useState<InputChipProps[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const chipInputRef = useRef<ChipInputForwardRef | null>(null);

  const { mutateAsync: updateOrganization } = useUpdateOrganizationMutation();
  const { mutateAsync: inviteMembers } = useInviteOrganizationMutation(
    currentOrganization?.id
  );

  const previousOrganizationIdRef = useRef(currentOrganization?.id);

  useEffect(() => {
    if (previousOrganizationIdRef.current === currentOrganization?.id) {
      return;
    }

    previousOrganizationIdRef.current = currentOrganization?.id;
    setOrgName(currentOrganization?.name ?? '');
  }, [currentOrganization?.id, currentOrganization?.name]);

  const addChip = (value: string) => {
    if (!value.length) {
      return;
    }
    try {
      emailValidation.schema.validateSync(value);
      setValidationError(null);
      setEmailChips((chips) => {
        const newChip: InputChipProps = {
          onDestroy: () => {
            setEmailChips((chips) =>
              chips.filter((chip) => !isEqual(chip, newChip))
            );
          },
          title: value,
        };
        return [...chips, newChip];
      });
    } catch (error) {
      if (error instanceof Error) {
        setValidationError(
          'message' in error ? error.message : 'Invalid email address'
        );
      }
    }
  };

  const clearAllChips = () => {
    setEmailChips([]);
  };

  const handleNext = async () => {
    setSubmitError(null);
    setIsSubmitting(true);

    try {
      if (
        currentOrganization &&
        orgName.trim() &&
        orgName.trim() !== currentOrganization.name
      ) {
        await updateOrganization({
          id: currentOrganization.id,
          name: orgName.trim(),
        });
      }

      const emails = uniqBy(emailChips, 'title').map((chip) => ({
        userEmail: chip.title,
      }));

      const inputValue = chipInputRef.current?.inputRef?.value;
      if (inputValue) {
        try {
          emailValidation.schema.validateSync(inputValue);
          emails.push({ userEmail: inputValue });
          chipInputRef.current?.clearInput();
        } catch {
          setSubmitError(
            'Please fix invalid email addresses before continuing.'
          );
          return;
        }
      }

      if (emails.length) {
        await inviteMembers(emails);
      }

      onNext();
    } catch {
      setSubmitError('Something went wrong while saving your organization.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CardShell
      title="Name your organization"
      titleVariant="h5"
      currentStep={1}
      totalSteps={3}
      flushRightBottom
      actions={
        <Button
          endIcon={<Icons.ArrowRight />}
          onClick={handleNext}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          Next
        </Button>
      }
      slots={{
        left: (
          <Stack gap={tokens.primitives.space[500]} width={540} pr={24}>
            <Input
              label="Organization name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              size="medium"
            />

            <Box>
              <Typography
                variant="h6"
                color={tokens.theme.fg.structural.primary}
                sx={{ mb: 4 }}
              >
                Need to invite your team?
              </Typography>
              <Typography
                variant="P14R"
                color={tokens.theme.fg.structural.secondary}
              >
                Invite unlimited teammates to your organization by email
              </Typography>
            </Box>

            <ChipInput
              ref={chipInputRef}
              label="Email Address"
              separators={['Enter', ',']}
              onSeparation={(value) => {
                if (Array.isArray(value)) {
                  value.forEach((v) => addChip(v));
                } else {
                  addChip(value);
                }
              }}
              placeholder="name@example.com"
              error={validationError ?? submitError}
              chips={emailChips}
              onClear={clearAllChips}
              multiline
              size="medium"
            />
          </Stack>
        ),
        right: <BrowserImage orgName={orgName} />,
      }}
    ></CardShell>
  );
};
